import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ReviewData {
  flowName: string;
  formData: Record<string, any>;
  depthProfile: string;
}

interface ReviewSection {
  id: string;
  icon: React.ReactElement;
  title: string;
  color: string;
  items: string[];
}

// ─── Build sections based on document context ────────────────────────────────

function buildReviewSections(
  flowName: string,
  formData: Record<string, any>,
  depthProfile: string,
): ReviewSection[] {
  const name = flowName.toLowerCase();
  const isPeticao =
    name.includes('petição') ||
    name.includes('peticao') ||
    name.includes('ação') ||
    name.includes('acao') ||
    name.includes('inicial');
  const isContestacao =
    name.includes('contestação') ||
    name.includes('contestacao') ||
    name.includes('defesa');
  const isRecurso =
    name.includes('recurso') ||
    name.includes('apelação') ||
    name.includes('agravo');
  const isContrato = name.includes('contrato') || name.includes('distrato');

  return [
    {
      id: 'dados',
      icon: <PersonSearchIcon fontSize="small" />,
      title: 'Dados das Partes',
      color: '#1565c0',
      items: [
        'Confirme nome completo, CPF/CNPJ e qualificação do(s) cliente(s)',
        'Verifique se o endereço do réu/requerido está correto e atualizado',
        'Cheque se há necessidade de incluir representante legal ou advogado constituído',
        formData.parte_contraria
          ? `Parte contrária informada: "${formData.parte_contraria}" — valide documentos`
          : 'Adicione qualificação completa da parte contrária se não preenchida',
      ],
    },
    {
      id: 'jurisprudencia',
      icon: <GavelIcon fontSize="small" />,
      title: 'Jurisprudência e Legislação',
      color: '#6a1b9a',
      items: [
        'Verifique se as decisões citadas são do tribunal competente para este caso',
        'Confirme se os acórdãos são recentes (preferencialmente últimos 5 anos)',
        'Valide se os artigos de lei citados estão vigentes e não foram alterados',
        'Considere adicionar jurisprudência do STJ/STF se houver repercussão geral',
        depthProfile === 'objetivo'
          ? 'Você escolheu perfil objetivo — considere ampliar a fundamentação doutrinária'
          : '',
      ].filter(Boolean),
    },
    {
      id: 'pedidos',
      icon: <ListAltIcon fontSize="small" />,
      title: 'Pedidos e Requerimentos',
      color: '#e65100',
      items: [
        'Certifique-se de que todos os pedidos estão claros e específicos (certo, líquido e determinado)',
        isPeticao ? 'Verifique se há pedido de tutela de urgência/antecipação se o caso exige' : '',
        isContestacao ? 'Confirme se a contestação impugnou especificamente cada ponto da inicial' : '',
        isRecurso
          ? 'Certifique-se de que as razões do recurso apontam claramente os vícios da decisão recorrida'
          : '',
        'Avalie se é necessário incluir pedido subsidiário como alternativa',
        'Revise o valor da causa — deve refletir o objeto do pedido principal',
      ].filter(Boolean),
    },
    {
      id: 'fatos',
      icon: <DescriptionIcon fontSize="small" />,
      title: 'Narrativa dos Fatos',
      color: '#2e7d32',
      items: [
        'Leia a narrativa e verifique se a cronologia dos fatos está correta',
        'Confirme datas, valores monetários e documentos mencionados nos fatos',
        !formData.fatos && !formData.descricao_fatos
          ? '⚠ Fatos não detalhados no wizard — revise e expanda esta seção no editor'
          : 'Fatos foram fornecidos — revise se a IA capturou todos os pontos relevantes',
        'Verifique se há fatos importantes que não foram mencionados no preenchimento',
        isContrato ? 'Confirme cláusulas específicas, prazos e obrigações das partes no contrato' : '',
      ].filter(Boolean),
    },
    {
      id: 'tecnico',
      icon: <RateReviewIcon fontSize="small" />,
      title: 'Revisão Técnica Final',
      color: '#c62828',
      items: [
        'Revise ortografia, concordância e linguagem jurídica adequada',
        'Verifique se o foro/juízo competente está correto para a matéria',
        'Confirme se a peça segue o modelo exigido pelo tribunal (formato, margens, fonte)',
        'Verifique se há necessidade de documentos a serem juntados como provas',
        'Leia o documento do início ao fim antes de protocolar ou enviar ao cliente',
      ],
    },
  ];
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

const CHECKS_KEY = (docId: string) => `review_checks_${docId}`;
const DATA_KEY = (docId: string) => `review_data_${docId}`;

export function saveReviewData(docId: string, data: ReviewData) {
  try {
    localStorage.setItem(DATA_KEY(docId), JSON.stringify(data));
  } catch {}
}

export function loadReviewData(docId: string): ReviewData | null {
  try {
    const raw = localStorage.getItem(DATA_KEY(docId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface ReviewChecklistProps {
  open: boolean;
  docId: string;
  reviewData: ReviewData;
  documentContent?: string;
  onClose: () => void;
  mode?: 'side' | 'top';
  onModeChange?: (mode: 'side' | 'top') => void;
  rightOffset?: number;
  topOffset?: number;
}

const ReviewChecklist: React.FC<ReviewChecklistProps> = ({
  open,
  docId,
  reviewData,
  documentContent = '',
  onClose,
  mode = 'side',
  onModeChange,
  rightOffset = 16,
  topOffset = 56,
}) => {
  const theme = useTheme();
  const { flowName, formData, depthProfile } = reviewData;

  // Campos a preencher: placeholders [CAMPO] ainda presentes no documento
  const pendingFields = useMemo(() => {
    if (!documentContent) return [] as string[];
    const text = documentContent.replace(/<[^>]+>/g, ' ');
    const matches = text.match(/\[[^\]\n]{2,60}\]/g) || [];
    return Array.from(new Set(matches));
  }, [documentContent]);

  const sections = useMemo(
    () => buildReviewSections(flowName, formData, depthProfile),
    [flowName, formData, depthProfile],
  );

  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(CHECKS_KEY(docId));
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Persist checks on every change
  useEffect(() => {
    try {
      localStorage.setItem(CHECKS_KEY(docId), JSON.stringify(checked));
    } catch {}
  }, [checked, docId]);

  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  const toggle = (key: string) => setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  const isTopMode = mode === 'top';

  if (!open) return null;

  return (
    <Paper
      elevation={12}
      sx={{
        position: 'fixed',
        zIndex: 1350,
        top: topOffset,
        right: rightOffset,
        bottom: isTopMode ? 'auto' : 16,
        left: isTopMode ? 16 : 'auto',
        width: isTopMode ? `calc(100vw - ${rightOffset + 32}px)` : { xs: 'calc(100vw - 32px)', lg: 380 },
        maxWidth: isTopMode ? 'none' : { lg: 380 },
        height: isTopMode ? '42vh' : `calc(100vh - ${topOffset + 24}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 3,
        background: theme.palette.mode === 'dark' ? '#1a1a2e' : '#fafafa',
        border: `2px solid ${alpha('#4caf50', 0.4)}`,
        boxShadow: `0 20px 60px ${alpha('#000', 0.16)}`,
        transition: 'all 0.22s ease',
      }}
    >
      <Box sx={{ pb: 0, pt: 3, px: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: 2, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
            }}
          >
            <AssignmentTurnedInIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Lista de Revisão — {flowName || 'Documento'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
              Marque cada item conforme revisar. Seu progresso é salvo automaticamente.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={isTopMode ? 'Voltar para a lateral' : 'Expandir acima do documento'}>
              <IconButton
                onClick={() => onModeChange?.(isTopMode ? 'side' : 'top')}
                size="small"
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              >
                {isTopMode ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isTopMode ? 'Fechar expandido e voltar para a lateral' : 'Fechar revisão'}>
              <IconButton
                onClick={() => {
                  if (isTopMode) onModeChange?.('side');
                  else onClose();
                }}
                size="small"
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ mt: 2, mb: 1, px: 0.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progresso da revisão
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              color={progress === 100 ? 'success.main' : 'text.secondary'}
            >
              {checkedCount}/{totalItems} itens verificados
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6, borderRadius: 3,
              backgroundColor: alpha('#4caf50', 0.15),
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background:
                  progress === 100
                    ? 'linear-gradient(90deg, #4caf50, #2e7d32)'
                    : 'linear-gradient(90deg, #42a5f5, #1565c0)',
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2, overflowY: 'auto', flex: 1 }}>
        <Alert
          icon={<LightbulbIcon fontSize="small" />}
          severity="info"
          sx={{ mb: 2, borderRadius: 2, fontSize: '0.85rem', position: 'sticky', top: 0, zIndex: 1 }}
        >
          A IA gerou o documento com base nas informações fornecidas.{' '}
          <strong>Contextos que não foram informados podem estar genéricos.</strong>{' '}
          Clique nos itens abaixo para marcar conforme revisar.
        </Alert>

        {pendingFields.length > 0 && (
          <Alert
            icon={<WarningAmberIcon fontSize="small" />}
            severity="warning"
            sx={{ mb: 2, borderRadius: 2, fontSize: '0.85rem' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {pendingFields.length} {pendingFields.length === 1 ? 'campo a preencher' : 'campos a preencher'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              O documento contém marcadores não preenchidos. Complete-os no editor antes de finalizar:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {pendingFields.slice(0, 30).map((f, i) => (
                <Chip key={i} label={f} size="small" color="warning" variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }} />
              ))}
            </Box>
          </Alert>
        )}

        {sections.map(section => (
          <Accordion
            key={section.id}
            defaultExpanded={section.id === 'dados' || section.id === 'jurisprudencia'}
            disableGutters
            elevation={0}
            sx={{
              mb: 1,
              border: `1px solid ${alpha(section.color, 0.2)}`,
              borderRadius: '10px !important',
              overflow: 'hidden',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { boxShadow: `0 2px 8px ${alpha(section.color, 0.15)}` },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: alpha(section.color, 0.06),
                minHeight: 48,
                '&.Mui-expanded': { minHeight: 48 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ color: section.color }}>{section.icon}</Box>
                <Typography variant="subtitle2" fontWeight={700} color={section.color}>
                  {section.title}
                </Typography>
                <Chip
                  label={`${section.items.filter((_, i) => checked[`${section.id}-${i}`]).length}/${section.items.length}`}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.7rem', fontWeight: 700,
                    backgroundColor: alpha(section.color, 0.12),
                    color: section.color,
                  }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List dense disablePadding>
                {section.items.map((item, i) => {
                  const key = `${section.id}-${i}`;
                  const isWarning = item.startsWith('⚠');
                  return (
                    <ListItem
                      key={key}
                      button
                      onClick={() => toggle(key)}
                      sx={{
                        px: 2, py: 0.8,
                        borderTop: i > 0 ? `1px solid ${alpha(section.color, 0.08)}` : 'none',
                        textDecoration: checked[key] ? 'line-through' : 'none',
                        opacity: checked[key] ? 0.55 : 1,
                        transition: 'all 0.15s ease',
                        '&:hover': { backgroundColor: alpha(section.color, 0.05) },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={!!checked[key]}
                          size="small"
                          sx={{
                            color: alpha(section.color, 0.5),
                            '&.Mui-checked': { color: section.color },
                            p: 0,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              color: isWarning ? '#c62828' : 'text.primary',
                              fontWeight: isWarning ? 600 : 400,
                              fontSize: '0.83rem',
                              lineHeight: 1.5,
                            }}
                          >
                            {item}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}

        {depthProfile === 'objetivo' && (
          <Alert
            icon={<WarningAmberIcon fontSize="small" />}
            severity="warning"
            sx={{ mt: 1, borderRadius: 2, fontSize: '0.82rem' }}
          >
            Você escolheu o perfil <strong>Objetivo</strong> — o documento foi gerado de forma
            enxuta. Considere expandir manualmente seções de fundamentação se a causa for complexa.
          </Alert>
        )}
        {depthProfile === 'extenso' && (
          <Alert
            icon={<CheckCircleIcon fontSize="small" />}
            severity="success"
            sx={{ mt: 1, borderRadius: 2, fontSize: '0.82rem' }}
          >
            Perfil <strong>Extenso</strong> utilizado — o documento foi gerado com máximo
            detalhamento técnico. Verifique se o tamanho está adequado ao tribunal.
          </Alert>
        )}
      </Box>

      <Box sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
        {progress === 100 ? (
          <Alert
            icon={<CheckCircleIcon fontSize="small" />}
            severity="success"
            sx={{ flex: 1, borderRadius: 2, fontSize: '0.82rem', mr: 1 }}
          >
            <strong>Revisão completa!</strong> Documento pronto para uso.
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary" flex={1}>
            O progresso é salvo automaticamente. Feche e retome a qualquer momento.
          </Typography>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
          {isTopMode && (
            <Button
              variant="outlined"
              onClick={() => onModeChange?.('side')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Voltar para lateral
            </Button>
          )}
          <Button
            variant="contained"
            onClick={isTopMode ? () => onModeChange?.('side') : onClose}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' },
            }}
          >
            {isTopMode ? 'Continuar na lateral' : 'Continuar editando'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

// ─── Toolbar button helper ────────────────────────────────────────────────────

export function getReviewProgress(docId: string, flowName: string, formData: Record<string, any>, depthProfile: string): { checked: number; total: number } {
  try {
    const sections = buildReviewSections(flowName, formData, depthProfile);
    const total = sections.reduce((acc, s) => acc + s.items.length, 0);
    const raw = localStorage.getItem(CHECKS_KEY(docId));
    const checks: Record<string, boolean> = raw ? JSON.parse(raw) : {};
    const checked = Object.values(checks).filter(Boolean).length;
    return { checked, total };
  } catch {
    return { checked: 0, total: 0 };
  }
}

export default ReviewChecklist;
